import axiosInstance from "@/api/axios";
import keycloak, { configToUse } from "@/Keycloak";
import {
  CreativeReportData,
  CreativeReportListItem,
  ChatDataConfig,
  CreativeReport,
  CreativeReportSection,
  SyncAssetsResponse,
  DataGraphResponse,
  AssignedLabelsResponse,
  UpdateAssignedLabelRequest,
  UpdateAssignedLabelResponse,
  FatigueSettings,
  ReportType,
  InsightPrompt,
  InsightType,
  CreativeOverviewResponse,
  AdDetailResponse,
} from "@/screens/creatives/types";
import {
  CreativeReportMetadata,
  CreativeSectionConfiguration,
  DataConfigurations,
} from "@/types";
import { CancelToken } from "axios";
import { ChatData, ChatHistory } from "@/components/common/copilot/types";

export const fetchCreativeReportList = async (
  accountId: string,
  reportType?: ReportType,
) => {
  const res = await axiosInstance.get<{
    status: string;
    reports: CreativeReportListItem[];
  }>(`/creative/${accountId}/report/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    params: {
      ...(reportType && { report_type: reportType }),
    },
  });
  return res.data;
};

export const fetchReportById = async (
  accountId: string,
  reportId: string,
  cancelToken?: CancelToken,
) => {
  const res = await axiosInstance.get<CreativeReport>(
    `/creative/${accountId}/report/${reportId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken,
    },
  );
  return res.data;
};

export const fetchSectionById = async (
  accountId: string,
  reportId: string,
  sectionId: string,
  cancelToken?: CancelToken,
) => {
  const res = await axiosInstance.get<CreativeReportSection>(
    `/creative/${accountId}/report/${reportId}/sections/${sectionId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken,
    },
  );
  return res.data;
};

export const createCreativeReport = async (
  accountId: string,
  data: Partial<CreativeReportListItem> & {
    section_configuration: Partial<CreativeSectionConfiguration>;
  },
) => {
  const res = await axiosInstance.post<{
    status: string;
    report: CreativeReport;
  }>(`/creative/${accountId}/report/`, data, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

export const updateCreativeReport = async (
  accountId: string,
  reportId: string,
  data: Partial<CreativeReportListItem>,
) => {
  const res = await axiosInstance.put<{ status: string; report: CreativeReportListItem }>(
    `/creative/${accountId}/report/${reportId}/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const duplicateCreativeReport = async (accountId: string, reportId: string) => {
  const res = await axiosInstance.post<CreativeReport>(
    `/creative/${accountId}/report/${reportId}/duplicate/`,
    {},
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const copyReportToAccounts = async (
  accountId: string,
  reportId: string,
  accountIds: string[],
) => {
  const res = await axiosInstance.post<{
    status: string;
    message: string;
    data: CreativeReport[];
  }>(
    `/creative/${accountId}/report/${reportId}/export/`,
    {
      account_ids: accountIds,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const deleteCreativeReport = async (accountId: string, reportId: string) => {
  const res = await axiosInstance.delete<{ status: string }>(
    `/creative/${accountId}/report/${reportId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const fetchCreativeReportData = async (data: {
  accountId: string;
  sectionId: string;
  sectionData?: CreativeSectionConfiguration;
  reportData?: CreativeReportMetadata;
  creative_type?: string;
  isPolling?: boolean;
  cancelToken?: CancelToken;
  operation_mode?: "update_only" | "fetch_only" | "both";
  video_retention?: boolean;
  use_new_breakdown_structure?: boolean;
  section_level_date?: boolean;
  apply_to_all_sections?: boolean;
  page?: number;
  page_size?: number;
}) => {
  const res = await axiosInstance.post<CreativeReportData>(
    `/creative/${data.accountId}/data/${data.sectionId}/`,
    {
      ...(data.reportData && { ...data.reportData }),
      ...(data.sectionData && { ...data.sectionData }),
      ...(data.creative_type && { creative_type: data.creative_type }),
      ...(data.isPolling && { is_polling: data.isPolling }),
      ...(data.operation_mode && { operation_mode: data.operation_mode }),
      ...(data.video_retention && { video_retention: data.video_retention }),
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
      params: {
        ...(data.use_new_breakdown_structure !== undefined && {
          use_new_breakdown_structure: data.use_new_breakdown_structure,
        }),
        ...(data.section_level_date !== undefined && {
          section_level_date: data.section_level_date,
        }),
        ...(data.apply_to_all_sections !== undefined && {
          apply_to_all_sections: data.apply_to_all_sections,
        }),
        ...(data.page !== undefined && { page: data.page }),
        ...(data.page_size !== undefined && { page_size: data.page_size }),
      },
    },
  );
  return res.data;
};

export const fetchAdDetailData = async (data: {
  accountId: string;
  sectionId: string;
  sectionData?: CreativeSectionConfiguration;
  group_value: string;
  ad_page?: number;
  ad_page_size?: number;
  cancelToken?: CancelToken;
}) => {
  const res = await axiosInstance.post<AdDetailResponse>(
    `/creative/${data.accountId}/data/${data.sectionId}/ads/`,
    {
      ...(data.sectionData && { ...data.sectionData }),
      group_value: data.group_value,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken: data.cancelToken,
      params: {
        ...(data.ad_page !== undefined && { ad_page: data.ad_page }),
        ...(data.ad_page_size !== undefined && { ad_page_size: data.ad_page_size }),
      },
    },
  );
  return res.data;
};

export const getDataConfigurations = async (
  accountId: string,
  cancelToken?: CancelToken,
) => {
  const res = await axiosInstance.get<{
    status: string;
    configuration: DataConfigurations;
  }>(`/creative/${accountId}/data-configurations/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    cancelToken,
  });
  return res.data;
};

export const getPublicDataConfiguration = async (
  accountId: string,
  reportId: string,
  cancelToken?: CancelToken,
) => {
  const res = await axiosInstance.get<{
    status: string;
    configuration: DataConfigurations;
  }>(`/creative/${accountId}/data-configurations/${reportId}/public/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    cancelToken,
  });
  return res.data;
};

export const sendCreativeChatMessageStreaming = (
  account_id: string | undefined,
  data: ChatDataConfig,
  signal?: AbortSignal,
) => {
  if (!account_id) return;
  return fetch(
    `${configToUse.VITE_APP_FERROUS_API_BASE_URL}/creative/${account_id}/chat/streaming/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${keycloak.token}`,
      },
      body: JSON.stringify(data),
      signal,
    },
  );
};

export const fetchCreativeChatList = async (account_id: string) => {
  const res = await axiosInstance.get<ChatData[]>(`/creative/${account_id}/chat/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

export const fetchCreativeChatHistory = async (
  account_id: string | undefined,
  chat_id: string | undefined,
): Promise<{
  id: string;
  chat_history: ChatHistory[];
}> => {
  const res = await axiosInstance.get<{
    id: string;
    chat_history: ChatHistory[];
  }>(`/creative/${account_id}/chat/${chat_id}/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

export const addSectionToReport = async (
  accountId: string,
  reportId: string,
  data: {
    title: string;
    icon?: string;
    configuration?: Partial<CreativeSectionConfiguration>;
    description?: string | null;
  },
) => {
  const res = await axiosInstance.post<CreativeReportSection>(
    `/creative/${accountId}/report/${reportId}/sections/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const updateReportSection = async (
  accountId: string,
  reportId: string,
  sectionId: string,
  data: {
    title?: string;
    icon?: string;
    configuration?: CreativeSectionConfiguration;
  },
) => {
  const res = await axiosInstance.put<CreativeReportSection>(
    `/creative/${accountId}/report/${reportId}/sections/${sectionId}/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const deleteReportSection = async (
  accountId: string,
  reportId: string,
  sectionId: string,
) => {
  const res = await axiosInstance.delete<{ status: string }>(
    `/creative/${accountId}/report/${reportId}/sections/${sectionId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};
export const duplicateReportSection = async (
  accountId: string,
  reportId: string,
  sectionId: string,
) => {
  const res = await axiosInstance.post<CreativeReportSection>(
    `/creative/${accountId}/report/${reportId}/sections/${sectionId}/duplicate/`,
    {},
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const fetchAssets = async (
  accountId: string,
  assetType: string,
  searchTerm: string = "",
  options: {
    signal?: AbortSignal;
  } = {},
) => {
  const res = await axiosInstance.get<string[]>(`/creative/${accountId}/search-assets/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    params: {
      asset_type: assetType,
      search_term: searchTerm,
    },
    signal: options.signal,
  });
  return res.data;
};

export const syncCreativeAssets = async (
  accountId: string,
  date: string,
): Promise<SyncAssetsResponse> => {
  const res = await axiosInstance.post<SyncAssetsResponse>(
    `/creative/${accountId}/sync-assets/`,
    {
      date,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      params: {
        receive_estimates: true,
      },
    },
  );
  return res.data;
};

export const retrySyncFailedAssets = async (accountId: string) => {
  const res = await axiosInstance.post<SyncAssetsResponse>(
    `/creative/${accountId}/sync-failed-assets/`,
    {},
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};
export const getUniqueLabels = async (
  accountId: string,
  labelId: string,
  sectionId?: string,
  cancelToken?: CancelToken,
) => {
  const res = await axiosInstance.get<{ status: string; unique_labels: string[] }>(
    `/creative/${accountId}/label/${labelId}/unique-labels/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      params: {
        ...(sectionId && { section_id: sectionId }),
      },
      cancelToken,
    },
  );
  return res.data;
};

export const getDataGraph = async (
  accountId: string,
  sectionId: string,
  interval: string,
  metadata: CreativeReportMetadata & CreativeSectionConfiguration,
  cancelToken?: CancelToken,
  useNewBreakdownStructure?: boolean,
  dataView?: string,
) => {
  // Create a copy of metadata and remove compare_date if it doesn't have valid start_date and end_date
  let cleanedMetadata = { ...metadata };

  if (
    !cleanedMetadata?.compare_date ||
    !cleanedMetadata?.compare_date?.start_date ||
    !cleanedMetadata?.compare_date?.end_date
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { compare_date, ...rest } = cleanedMetadata;
    cleanedMetadata = rest as typeof cleanedMetadata;
  }

  const response = await axiosInstance.post<DataGraphResponse>(
    `/creative/${accountId}/data-graph/${sectionId}/`,
    { metadata: cleanedMetadata, interval_type: interval },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      params: {
        interval_type: interval,
        ...(useNewBreakdownStructure !== undefined && {
          use_new_breakdown_structure: useNewBreakdownStructure,
        }),
        ...(dataView !== undefined && {
          data_view: dataView,
        }),
      },
      cancelToken,
    },
  );
  return response.data;
};

export const updateSectionOrders = async (
  accountId: string,
  reportId: string,
  sectionOrders: Array<{
    section_id: string;
    order: number;
  }>,
) => {
  const res = await axiosInstance.put<{
    status: string;
    sections: CreativeReportSection[];
  }>(
    `/creative/${accountId}/report/${reportId}/sections/`,
    {
      section_orders: sectionOrders,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const updateReportOrders = async (
  accountId: string,
  reportOrders: Array<{
    report_id: string;
    order: number;
  }>,
  reportType: ReportType | "competitor",
) => {
  const res = await axiosInstance.post<{
    status: string;
    reports: CreativeReportListItem[];
  }>(
    `/creative/${accountId}/report/reorder/`,
    {
      report_orders: reportOrders,
      report_type: reportType,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const getAssignedLabels = async (
  accountId: string,
  labelConfigId: string,
  cancelToken?: CancelToken,
) => {
  const res = await axiosInstance.get<AssignedLabelsResponse>(
    `/creative/${accountId}/label/${labelConfigId}/assigned-labels/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken,
    },
  );
  return res.data;
};

export const updateAssignedLabel = async (
  accountId: string,
  labelConfigId: string,
  data: UpdateAssignedLabelRequest,
  cancelToken?: CancelToken,
) => {
  const res = await axiosInstance.put<UpdateAssignedLabelResponse>(
    `/creative/${accountId}/label/${labelConfigId}/assigned-labels/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken,
    },
  );
  return res.data;
};

export const updateFatigueSettings = async (accountId: string, data: FatigueSettings) => {
  const res = await axiosInstance.post<{ status: string }>(
    `/creative/${accountId}/fatigue-settings/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const syncReport = async (accountId: string, reportId: string) => {
  const res = await axiosInstance.post<{ status: string }>(
    `/creative/${accountId}/sync-report/${reportId}/`,
    null,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const syncSection = async (accountId: string, sectionId: string) => {
  const res = await axiosInstance.post<{ status: string }>(
    `/creative/${accountId}/sync-section/${sectionId}/`,
    null,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

// Generate Ad from a competitor ad reference
export const generateAd = async (
  accountId: string,
  data: {
    ad_id: string;
    prompt: string;
  },
) => {
  const res = await axiosInstance.post<{ status: string }>(
    `/creative/${accountId}/generate-ad/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const fetchInsightPrompts = async (
  accountId: string,
  type: InsightType,
  cancelToken?: CancelToken,
): Promise<InsightPrompt[]> => {
  const res = await axiosInstance.get<InsightPrompt[]>(
    `/creative/${accountId}/insight-prompts/?type=${type}`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      cancelToken,
    },
  );
  return res.data;
};

// Clear creative platform cache for an account
export const clearPlatformCache = async (
  accountId: string,
  data: CreativeSectionConfiguration & CreativeReportMetadata,
) => {
  const res = await axiosInstance.delete<{ status: string }>(
    `/creative/${accountId}/platform-cache/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      data,
    },
  );
  return res.data;
};

// Fetch creative overviews
export const fetchCreativeOverviews = async (
  accountId: string,
  creativeId: string,
  promptIds?: string[],
  cancelToken?: CancelToken,
): Promise<CreativeOverviewResponse> => {
  const params: Record<string, string> = {};
  if (promptIds && promptIds.length > 0) {
    params.prompt_ids = promptIds.join(",");
  }

  const res = await axiosInstance.get<CreativeOverviewResponse>(
    `/creative/${accountId}/creative/${creativeId}/overviews/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      params,
      cancelToken,
    },
  );
  return res.data;
};
